<template>
  <custom-dialog
    ref="dialog"
    store-modal
    color="primary"
    width="600px"
    :icon="generateMessagePath('icon')"
    :title="generateMessagePath('title')"
  >
    <v-form
      ref="form"
      v-model="valid"
      @input="$emit('valid', valid)"
      lazy-validation
    >
      <v-container>
        <v-row>
          <v-col cols="12" sm="8">
            <custom-properties-selector
              :disabled="!!currentActionPlan.node_id"
              :rules="[
                (v) => !!v || generateMessage('actions.create.noproperty'),
              ]"
              @newItem="handlePropertyChange"
              v-model="item.node_id"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-checkbox
              class="ma-0 pa-0"
              :label="generateMessage('actions.create.onsite_intervention')"
              v-model="item.on_site"
              hide-details
              dense
            ></v-checkbox>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" v-if="isEditing">
            <custom-date-item-selector
              v-model="item.action_status"
              input-date="datetime"
              input-item="type_id"
              namespace="actions/status"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6">
            <v-autocomplete
              :items="priorities"
              item-text="name"
              :rules="[
                (v) => !!v || generateMessage('actions.create.nopriority'),
              ]"
              item-value="id"
              v-model="item.priority_id"
              :label="generateMessage('actions.create.priorities') + ' *'"
              required
              dense
              outlined
              @input="updateDeadline"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6">
            <v-autocomplete
              :items="types"
              item-text="name"
              dense
              item-value="id"
              v-model="item.type_id"
              :rules="[(v) => !!v || generateMessage('actions.create.notype')]"
              :label="generateMessage('actions.create.type') + ' *'"
              required
              outlined
            ></v-autocomplete>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              :label="generateMessage('actions.create.start_date')"
              v-model="item.start_datetime"
              type="date"
              :rules="[
                (v) => !!v || generateMessage('actions.create.nodt_date'),
              ]"
              dense
              outlined
              @input="updateDeadline"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              :label="generateMessage('actions.create.deadline_datetime')"
              v-model="item.deadline_datetime"
              :min="item.start_datetime"
              dense
              outlined
              type="date"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <custom-text-area
              @paste="addDocument"
              @input="clearMessageDesc"
              required
              v-model="item.description"
              :loading="loadingDesc"
              :error-messages="messageDesc"
              :label="generateMessage('actions.create.description') + ' *'"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="12">
            <technician-selection-dialog
              multiple
              ref="techSelector"
              v-model="item.staffs_in_charge_id"
              :entity-id="currentActionPlan.entity_nav_full_id"
              :property-id="propertyId"
              :placeholder="generateMessage('actions.create.authors')"
              :rules="[
                (v) =>
                  !!v.length ||
                  generateMessage('actions.create.no_person_in_charge'),
              ]"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-checkbox
              dense
              class="ma-0 pa-0"
              hide-details
              v-model="mailChecked"
              :label="generateMessage('actions.create.mail.send')"
            />
          </v-col>
          <v-col>
            <v-hover v-slot="{ hover }">
              <v-checkbox
                dense
                class="ma-0 pa-0"
                hide-details
                v-model="item.daybook"
                :disabled="daybookState"
                :label="daybookHover(hover)"
              />
            </v-hover>
            <v-checkbox
              v-if="daybookState & isEditing"
              dense
              class="ma-0 pa-0"
              hide-details
              v-model="item.daybook_forced"
              :label="generateMessage('actions.create.daybook_forced')"
            />
            <!-- MYP-740 : En attente de l'activation de la fonctionnalité dès qu'on a le générateur de pdf
            <v-checkbox
              dense
              class="ma-0 pa-0"
              hide-details
              v-model="item.customer_shared_description"
              :label="
                generateMessage('actions.create.customer_shared_description')
              "
            />
            -->
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <template v-slot:actions>
      <v-btn
        :disabled="!valid"
        small
        color="success"
        class="mx-5"
        @click="handleValidateClick"
        :loading="isEditing ? isLoadingUpdate : isLoadingCreate"
      >
        {{ generateMessagePath('validate') }}
      </v-btn>
    </template>
  </custom-dialog>
</template>

<script>
import CustomTextArea from '@/components/CustomTextArea';
import CustomPropertiesSelector from '@/components/selects/CustomPropertiesSelector';
import CustomDialog from '@/components/dialogs/CustomDialog';
import formComponent from '@/business/mixins/form';
import TechnicianSelectionDialog from '@/components/selects/technician/TechnicianSelectionDialog';
import CustomDateItemSelector from '@/components/selects/CustomDateItemSelector';
import { mapGetters } from 'vuex';
import { generateMessage } from '@/utils/i18n';
import {
  formatDateToInputDate,
  getCurrentLocalDate,
  getCurrentLocalDateTime,
} from '@/business/utils/dates';
import { uploadDocumentFromPaste } from '@/services/services/documentsService';
import { findTechByGroupId } from '@/services/api/internal/requestActions/directReport.api';
import { ACTIONPLAN_ACTIONS_ITEM_ROUTE_NAME } from '@/router/actions.router';
import AuthorsService from '@/services/services/authors.service';
import ActionsService from '@/services/services/actions.service';
import { ACTIONS_LIST_ROUTE_NAME } from '@/router/rootActions.router';
import { formatActionItem } from '@/store/modules/actionplans/actions/plugins';

export default {
  name: 'ActionFormDialog',
  mixins: [
    formComponent({
      namespace: 'actionplans/actions',
    }),
  ],
  components: {
    CustomPropertiesSelector,
    CustomDialog,
    TechnicianSelectionDialog,
    CustomTextArea,
    CustomDateItemSelector,
  },
  data() {
    return {
      ACTIONS_LIST_ROUTE_NAME,
      loadingDesc: false,
      messageDesc: [],
      propertyId: null,
      daybookState: false,
    };
  },
  computed: {
    ...mapGetters({
      types: 'actions/types/items',
      priorities: 'actions/priorities/items',
      status: 'actions/status/items',
      currentActionPlan: 'actionplans/current',
    }),
    groupId() {
      return this.$store.getters['global/groupId'];
    },
    actionId() {
      return this.$store.getters['documents/actionId'];
    },
  },
  watch: {
    newItem(builtItem) {
      if (builtItem && builtItem.id) {
        this.processOpenOutlook(builtItem);
        this.$router.push({
          name: ACTIONPLAN_ACTIONS_ITEM_ROUTE_NAME,
          params: {
            actionplanId: builtItem.action_plan_id,
            actionId: builtItem.id,
          },
        });
      }
    },
    async updateItem(builtItem) {
      if (builtItem && builtItem.id) {
        this.processOpenOutlook(builtItem);
      }
      if (builtItem.current_act_status_type_name === 'Terminé') {
        this.$router.push({
          name: ACTIONPLAN_ACTIONS_ITEM_ROUTE_NAME,
          params: {
            actionplanId: builtItem.action_plan_id,
            actionId: builtItem.id,
          },
          query: { tab: 'gains' },
        });
      }
    },
    currentActionPlan: {
      handler() {
        this.mailChecked = false;
        if (!this.oldItem) {
          this.initItemFromNullItem();
          this.$refs.form.resetValidation();
        } else {
          this.initItemFromPartialItem(this.oldItem);
        }
      },
      deep: true,
    },
  },
  methods: {
    daybookHover(hover) {
      return hover
        ? this.generateMessage('actions.create.daybook_hover')
        : this.generateMessage('actions.create.daybook');
    },
    generateMessage,
    findTechByGroupId,
    getCurrentLocalDate,
    getCurrentLocalDateTime,
    uploadDocument: uploadDocumentFromPaste,
    processOpenOutlook(builtItem) {
      if (this.mailChecked) {
        const errorHandler = () => {
          this.$store.dispatch('snackbar/setSnackbarOpen', {
            message: this.$t('message.actions.create.mail.error'),
            color: 'warning',
            closeable: true,
          });
        };
        ActionsService.openOutlook(builtItem, this.isEditing, errorHandler);
        this.createUserExchange(builtItem, this.isEditing);
      }
    },
    async createUserExchange(builtItem, isEditing) {
      const authors = await AuthorsService.findAuthorsByOriginalIdx(
        builtItem.persons_in_charge.map((el) => String(el.staff_id))
      );
      const userExchange = {
        description: `Envoi de mail lors de la ${
          isEditing ? 'modification' : 'création'
        } de l'action`,
        datetime: this.getCurrentLocalDateTime(),
        exchange_type: 2,
        action_id: builtItem.id,
        author_idx: authors.map((aut) => aut.id),
        logbook_entry_id: null,
      };
      await this.$store.dispatch(
        `actionplans/actions/user_exchanges/createItem`,
        userExchange
      );
    },
    generateMessagePath(key) {
      return this.generateMessage(
        `actions.${this.isEditing ? 'update' : 'create'}.${key}`
      );
    },
    handleValidateClick() {
      this.validate();
    },
    addDocument(evt) {
      if (
        evt.clipboardData.files.length !== 0 &&
        evt.clipboardData.files[0].type.startsWith('image')
      ) {
        if (this.isEditing) {
          this.loadingDesc = true;
          this.messageDesc = [];
          const params = {
            propertyId: this.propertyId,
            actionId: this.item.id || this.actionId,
            groupId: this.groupId,
            file: evt.clipboardData.files[0],
          };
          this.uploadDocument(params)
            .then((uid) => {
              this.loadingDesc = false;
              this.item.description = this.item.description.concat(
                `~~img-uid:${uid}~~`
              );
            })
            .catch((err) => {
              // eslint-disable-next-line no-console
              console.log(err);
              this.loadingDesc = false;
              if (
                err &&
                err.length &&
                err.find((error) => error.code === 'Property not found')
              ) {
                this.messageDesc = `Impossible de récupérer les documents cette installation, merci de contacter le support Emedia.`;
              } else {
                this.messageDesc = "Echec de l'ajout de l'image à eMedia";
              }
            });
        } else {
          this.messageDesc =
            "Vous ne pouvez pas associer une image à une action qui n'a pas encore été créée.";
        }
        evt.preventDefault();
      }
    },
    setPropertyId(propertyId) {
      this.propertyId = propertyId;
    },
    handlePropertyChange(property) {
      this.setPropertyId(property.id);
    },
    clearMessageDesc() {
      this.messageDesc = [];
    },
    async initItemFromNullItem() {
      this.setPropertyId(this.currentActionPlan.node_associated_id);
      this.item = {
        action_plan_id: this.currentActionPlan.id,
        node_id: this.currentActionPlan.node_id,
        staffs_in_charge_id: [],
        priority_id: null,
        start_datetime: this.getCurrentLocalDate(),
        deadline_datetime: null,
        documents_number: 0,
        description: '',
        type_id: null,
        on_site: null,
        daybook: false,
        customer_shared_description: false,
      };
      const user = await this.findTechByGroupId(this.groupId);
      this.item.staffs_in_charge_id = user ? [user.id] : [];
    },
    initItemFromPartialItem(partialItem) {
      this.setPropertyId(partialItem.node_associated_id);
      this.item = formatActionItem(partialItem);
      this.daybookState = !!this.item.daybook;
    },
    async reset() {
      await this.initItemFromNullItem();
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    updateDeadline() {
      const tmp = new Date(this.item.start_datetime);
      const priority = this.priorities.find(
        (item) => item.id === this.item.priority_id
      );
      if (priority) {
        tmp.setDate(tmp.getDate() + priority.alert_in_days || 0);
      }
      this.item.deadline_datetime = formatDateToInputDate(tmp);
    },
  },
};
</script>
